import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Plus, Sparkles, Trash2, UserRound } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import B2BPageShell, { B2BPageFooterAction } from '../../../components/b2b/B2BPageShell';
import { IconSpecial, Toast } from '../../../components/ui';
import { GenerationProgressView } from '../components/GenerationProgressView';
import { ErrorBanner, PersonaState } from '../components/PersonaShared';
import {
  personaAbTestApi,
  personaInterviewApi,
  personaTestApi,
  resolvePersonaApiErrorMessage,
} from '../services/personaService';
import '../styles/persona.scss';

const toRowKey = (type, id) => `${type}:${id}`;
const DISPLAY_TYPE_INTERVIEW = '심층인터뷰';
const TEST_LIST_PAGE_SIZE = 10;
const PURPOSE_GOAL_MAX = 300;
const PURPOSE_PRODUCT_MAX = 200;
const PURPOSE_RUN_STAGES = [
  { label: '목적 분석', message: '조사 목적을 바탕으로 인터뷰 질문과 방향을 구성하고 있습니다.' },
  { label: '퍼소나 인터뷰', message: '선택된 퍼소나와 1:1 심층 인터뷰를 시뮬레이션하고 있습니다.' },
  { label: '인사이트 합성', message: '퍼소나별 결과를 교차 분석해 인사이트 보고서를 작성하고 있습니다.' },
];
const PURPOSE_RUN_NOTICE = '목적만 입력하면 인터뷰 실행부터 인사이트 보고서까지 자동으로 진행됩니다. 완료까지 약 2~3분 소요될 수 있습니다.';

const getTestScopeLabel = (test) => {
  const scopeType = test?.scope_type ?? test?.scopeType;
  const mode = test?.mode;
  if (scopeType === 'flow' || mode === 'flow') return '사용자 플로우';
  return '단일 화면';
};

const getRowDisplayType = (test, testType) => {
  if (testType === 'interview') return test?.typeLabel || DISPLAY_TYPE_INTERVIEW;
  if (testType === 'ab') return `A/B테스트 > ${getTestScopeLabel(test)}`;
  return `화면 사용성 검증 > ${getTestScopeLabel(test)}`;
};

const formatTestDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const normalizeStatus = (status) => {
  if (status === 'completed') return 'completed';
  if (status === 'completed_with_errors') return 'completed';
  if (status === 'failed') return 'failed';
  if (status === 'running' || status === 'pending' || status === 'draft') return 'running';
  return status || 'running';
};

const testStatusLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'completed') return '완료';
  if (normalized === 'failed') return '실패';
  if (normalized === 'running') return '생성 중';
  return status || '-';
};

const getRowTestType = (test) => {
  const kind = test?.kind || test?.testType || test?.type;
  if (kind === 'ab-test' || kind === 'ab') return 'ab';
  if (kind === 'interview') return 'interview';
  return 'ui';
};

const getRowDisplayStatus = (row) => {
  const testType = getRowTestType(row);
  const status = row?.status;
  if (testType === 'interview') {
    if (status === 'failed') return 'failed';
    if (status === 'completed_with_errors') {
      const summary = row.summary || {};
      const completedResponses = summary.completedResponses ?? summary.completed_responses ?? 0;
      if (completedResponses === 0) return 'failed';
    }
  }
  return normalizeStatus(status);
};

const TEST_TYPE_OPTIONS = [
  {
    value: 'ui',
    icon: 'participantRecruit',
    title: '화면 사용성 검증',
    description: ['퍼소나의 시각에서 화면 UI의 문제점을 진단하고', '사용성을 검증합니다.'],
  },
  {
    value: 'ab',
    icon: 'surveyAnalysis',
    title: 'A/B 비교검증',
    description: ['시안의 A/B 비교검증을 통해 퍼소나가 선호하는', '최적의 안을 제안합니다.'],
  },
  {
    value: 'interview',
    icon: 'participantScreener',
    title: '1:1 AI 인터뷰',
    description: ['타겟 퍼소나와의 심층 인터뷰를 진행하고', '핵심 인사이트를 도출합니다.'],
  },
];

function DeleteTestsModal({ deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="test-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="test-delete-modal-title">선택한 테스트를 삭제하시겠습니까?</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>삭제 시 관련 해당 테스트의 정보는 모두 삭제되며</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function TestTypeModal({ selectedType, onSelect, onCancel, onStart }) {
  const selectedOption = TEST_TYPE_OPTIONS.find((option) => option.value === selectedType);
  const startDisabled = selectedOption?.disabled;

  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-test-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="test-type-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="test-type-modal-title">테스트 유형 선택</h2>
        </header>
        <div className="persona-test-type-modal__body">
          {TEST_TYPE_OPTIONS.map((option) => {
            const selected = selectedType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`persona-test-type-option ${selected ? 'selected' : ''}`}
                onClick={() => onSelect(option.value)}
              >
                <IconSpecial name={option.icon} className="persona-test-type-option__icon" />
                <span className="persona-test-type-option__copy">
                  <strong>
                    {option.title}
                    {option.badge ? <em className="persona-test-type-option__badge">{option.badge}</em> : null}
                  </strong>
                  <span>
                    {option.description.map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </span>
                </span>
                <span className={`persona-test-type-radio ${selected ? 'selected' : ''}`} aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <footer>
          <button type="button" onClick={onCancel}>
            취소하기
          </button>
          <button type="button" onClick={onStart} disabled={startDisabled}>
            선택하기
          </button>
        </footer>
      </section>
    </div>
  );
}

function getPaginationItems(page, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }).map((_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const orderedPages = Array.from(pages)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((a, b) => a - b);

  return orderedPages.reduce((items, item, index) => {
    if (index > 0 && item - orderedPages[index - 1] > 1) {
      items.push(`ellipsis-${orderedPages[index - 1]}-${item}`);
    }
    items.push(item);
    return items;
  }, []);
}

function ListPagination({ page, totalPages, onChange }) {
  const items = getPaginationItems(page, totalPages);

  return (
    <nav className="persona-list-pagination" aria-label="테스트 목록 페이지">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-label="이전 페이지"
      >
        <ChevronLeft size={16} />
      </button>
      <div>
        {items.map((item) => (
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              className={item === page ? 'active' : ''}
              onClick={() => onChange(item)}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ) : (
            <span key={item}>...</span>
          )
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        aria-label="다음 페이지"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}

function QuickInsightModal({
  goal,
  product,
  length,
  error,
  running,
  onGoalChange,
  onProductChange,
  onLengthChange,
  onSubmit,
  onCancel,
}) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section className="persona-tests-purpose-panel" role="dialog" aria-modal="true" aria-labelledby="quick-insight-title">
        <header className="persona-tests-purpose-panel__head">
          <Sparkles size={18} />
          <div>
            <strong id="quick-insight-title">빠른 인사이트 생성 <em>Beta</em></strong>
            <p>목적을 바탕으로 질문 설계, 퍼소나 인터뷰, 교차 분석을 자동으로 진행합니다. 최대 6명이 참여합니다.</p>
          </div>
        </header>
        {error ? <p className="persona-tests-purpose-error" role="alert">{error}</p> : null}
        <label className="persona-tests-purpose-field">
          <span>조사 목적</span>
          <textarea
            value={goal}
            onChange={(event) => onGoalChange(event.target.value.slice(0, PURPOSE_GOAL_MAX))}
            placeholder="예: 멤버십 혜택이 실제 라이프스타일에 맞는지 확인하고, 추가로 원하는 서비스를 파악하고 싶습니다."
            rows={4}
            disabled={running}
          />
          <em>{goal.length}/{PURPOSE_GOAL_MAX}</em>
        </label>
        <label className="persona-tests-purpose-field">
          <span>제품/서비스 설명 (선택)</span>
          <textarea
            value={product}
            onChange={(event) => onProductChange(event.target.value.slice(0, PURPOSE_PRODUCT_MAX))}
            placeholder="어떤 제품/서비스를 검증하는지 짧게 적어 주세요."
            rows={2}
            disabled={running}
          />
          <em>{product.length}/{PURPOSE_PRODUCT_MAX}</em>
        </label>
        <div className="persona-tests-purpose-depth">
          <span>인터뷰 깊이</span>
          <div>
            {[
              { id: 'quick', label: '빠른' },
              { id: 'standard', label: '표준' },
              { id: 'deep', label: '심층' },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                className={length === option.id ? 'active' : ''}
                onClick={() => onLengthChange(option.id)}
                disabled={running}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <footer className="persona-tests-purpose-panel__footer">
          <button type="button" className="persona-tests-purpose-cancel" onClick={onCancel} disabled={running}>취소</button>
          <button
            type="button"
            className="persona-tests-purpose-submit"
            onClick={onSubmit}
            disabled={running || !goal.trim()}
          >
            <Sparkles size={18} />
            인터뷰 실행 & 인사이트 생성
          </button>
        </footer>
      </section>
    </div>
  );
}

function TestsListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tests, setTests] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: TEST_LIST_PAGE_SIZE, total_count: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState('ui');
  const [deleting, setDeleting] = useState(false);
  const [retryingRows, setRetryingRows] = useState(new Set());
  const [listPage, setListPage] = useState(1);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'success' });
  const [purposeGoal, setPurposeGoal] = useState('');
  const [purposeProduct, setPurposeProduct] = useState('');
  const [purposeLength, setPurposeLength] = useState('standard');
  const [purposeRunning, setPurposeRunning] = useState(false);
  const [purposeStage, setPurposeStage] = useState(0);
  const [showQuickInsightModal, setShowQuickInsightModal] = useState(false);
  const rowCacheRef = useRef(new Map());
  const loadRequestIdRef = useRef(0);

  const loadData = useCallback(async (page = listPage) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const result = await personaTestApi.listSummaries({ page, pageSize: TEST_LIST_PAGE_SIZE });
      if (requestId !== loadRequestIdRef.current) return;
      const nextPagination = {
        page: result.pagination.page || page,
        page_size: result.pagination.page_size || TEST_LIST_PAGE_SIZE,
        total_count: result.pagination.total_count || 0,
        total_pages: result.pagination.total_pages || 1,
      };
      setTests(result.items);
      setPagination(nextPagination);
      if (nextPagination.page !== page) setListPage(nextPagination.page);
    } catch (err) {
      if (requestId === loadRequestIdRef.current) {
        setError(resolvePersonaApiErrorMessage(err, '테스트 목록을 불러오지 못했습니다.'));
      }
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [listPage]);

  useEffect(() => {
    loadData(listPage);
  }, [loadData, listPage]);

  useEffect(() => {
    const toastState = location.state?.toast;
    if (!toastState?.message) return;
    setToast({
      isOpen: true,
      message: toastState.message,
      type: toastState.type || 'success',
    });
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!purposeRunning) return undefined;
    const timer = window.setInterval(() => {
      setPurposeStage((current) => Math.min(current + 1, PURPOSE_RUN_STAGES.length - 1));
    }, 12000);
    return () => window.clearInterval(timer);
  }, [purposeRunning]);

  const rows = useMemo(() => {
    const normalizedRows = tests.map((row) => {
      const testType = getRowTestType(row);
      return {
        ...row,
        testType,
        rowKey: toRowKey(testType, row.id),
        displayType: getRowDisplayType(row, testType),
        persona_count: row.persona_count ?? row.personaCount ?? row.persona_ids?.length ?? row.personaIds?.length ?? row.context_data?.persona_ids?.length,
        created_at: row.created_at || row.createdAt,
      };
    });

    return normalizedRows.sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [tests]);

  const listPageCount = Math.max(1, pagination.total_pages || 1);
  const normalizedListPage = pagination.page || listPage;
  const pagedRows = rows;

  useEffect(() => {
    rows.forEach((row) => rowCacheRef.current.set(row.rowKey, row));
  }, [rows]);

  const selectedCount = selectedRows.size;
  const allRowsSelected = pagedRows.length > 0 && pagedRows.every((row) => selectedRows.has(row.rowKey));

  const openRow = (row) => {
    if (getRowDisplayStatus(row) === 'running') return;
    if (row.testType === 'ab') {
      navigate(`/tests/ab/${row.id}`);
      return;
    }
    if (row.testType === 'interview') {
      navigate(`/tests/interview/${row.id}`);
      return;
    }
    navigate(`/tests/ui/${row.id}`);
  };

  const toggleRow = (rowKey) => {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const toggleAllRows = () => {
    setSelectedRows((current) => {
      const next = new Set(current);
      pagedRows.forEach((row) => {
        if (allRowsSelected) next.delete(row.rowKey);
        else next.add(row.rowKey);
      });
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedCount === 0) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedCount === 0 || deleting) return;
    const selected = Array.from(selectedRows)
      .map((rowKey) => rowCacheRef.current.get(rowKey))
      .filter(Boolean);
    if (selected.length === 0) return;
    setDeleting(true);
    setError('');
    try {
      await Promise.all(selected.map((row) => (
        row.testType === 'ab'
          ? personaAbTestApi.deleteTest(row.id)
          : row.testType === 'interview'
            ? personaInterviewApi.deleteInterview(row.id)
            : personaTestApi.deleteTest(row.id)
      )));
      selected.forEach((row) => rowCacheRef.current.delete(row.rowKey));
      setSelectedRows(new Set());
      setShowDeleteModal(false);
      setToast({ isOpen: true, message: '선택한 테스트를 삭제했습니다.', type: 'success' });
      const remainingCount = Math.max(0, pagination.total_count - selected.length);
      const nextPage = Math.min(
        listPage,
        Math.max(1, Math.ceil(remainingCount / TEST_LIST_PAGE_SIZE)),
      );
      if (nextPage !== listPage) setListPage(nextPage);
      else await loadData(nextPage);
    } catch (err) {
      setError(err.message || '선택한 테스트를 삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = async (event, row) => {
    event.stopPropagation();
    const status = getRowDisplayStatus(row);
    if (status === 'failed') {
      if (retryingRows.has(row.rowKey)) return;
      setRetryingRows((current) => {
        const next = new Set(current);
        next.add(row.rowKey);
        return next;
      });
      setError('');
      try {
        if (row.testType === 'ab') {
          await personaAbTestApi.runTest(row.id);
        } else if (row.testType === 'interview') {
          await personaInterviewApi.runInterview(row.id);
        } else {
          await personaTestApi.runTest(row.id);
        }
        await loadData();
      } catch (err) {
        setError(resolvePersonaApiErrorMessage(err, '테스트를 재시도하지 못했습니다.'));
      } finally {
        setRetryingRows((current) => {
          const next = new Set(current);
          next.delete(row.rowKey);
          return next;
        });
      }
      return;
    }
    if (status === 'completed') {
      openRow(row);
    }
  };

  const handleOpenTypeModal = () => {
    setSelectedTestType('ui');
    setShowTypeModal(true);
  };

  const handleStartTest = () => {
    const selectedOption = TEST_TYPE_OPTIONS.find((option) => option.value === selectedTestType);
    if (!selectedOption || selectedOption.disabled) return;
    setShowTypeModal(false);
    if (selectedTestType === 'quick-insight') {
      setShowQuickInsightModal(true);
      return;
    }
    if (selectedTestType === 'interview') {
      navigate('/tests/interview/new');
      return;
    }
    navigate(`/tests/new?type=${selectedTestType}`);
  };

  const handlePurposeRun = async () => {
    if (purposeRunning || !purposeGoal.trim()) return;
    setPurposeRunning(true);
    setPurposeStage(0);
    setError('');
    try {
      const result = await personaInterviewApi.purposeRun({
        goal: purposeGoal.trim(),
        productDescription: purposeProduct.trim() || undefined,
        length: purposeLength,
      });
      setShowQuickInsightModal(false);
      const interviewId = result?.interview?.id;
      if (!interviewId) {
        throw new Error('인터뷰 결과를 받지 못했습니다.');
      }
      if (result.insightError) {
        navigate(`/tests/interview/${interviewId}`, {
          state: { toast: { message: '인터뷰는 완료됐지만 인사이트 생성에 실패했습니다.', type: 'error' } },
        });
        return;
      }
      navigate(`/tests/interview/${interviewId}/insights`);
    } catch (err) {
      setError(resolvePersonaApiErrorMessage(err, '목적 기반 인터뷰 실행에 실패했습니다.'));
      setPurposeRunning(false);
      setPurposeStage(0);
    }
  };

  if (purposeRunning) {
    return (
      <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
        <main className="persona-generation-progress">
          <GenerationProgressView
            stages={PURPOSE_RUN_STAGES}
            currentStep={purposeStage}
            notice={PURPOSE_RUN_NOTICE}
            ariaLabel="목적 기반 인터뷰 진행 상태"
          />
        </main>
      </B2BLayout>
    );
  }

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <B2BPageShell
        className="persona-tests-page"
        title="테스트 목록"
        description="실행된 테스트의 내역을 관리하고, 도출된 결과 인사이트를 한눈에 확인하세요."
        contentClassName="persona-tests-content"
        footer={(
          <B2BPageFooterAction leftIcon={<Plus size={24} strokeWidth={1.8} />} onClick={handleOpenTypeModal}>
            새 테스트 생성
          </B2BPageFooterAction>
        )}
      >
        <ErrorBanner message={error} />

        <div className="persona-tests-list-header">
          <strong>{selectedCount > 0 ? `선택된 테스트 ${selectedCount}` : `총 테스트 ${pagination.total_count}`}</strong>
          <button
            type="button"
            className="persona-tests-delete-button"
            onClick={handleDeleteSelected}
            disabled={selectedCount === 0 || deleting}
          >
            <Trash2 size={18} />
            삭제
          </button>
        </div>

        {loading ? (
          <PersonaState type="loading" title="불러오는 중" message="테스트 목록을 가져오고 있습니다." />
        ) : rows.length === 0 ? (
          <div className="persona-tests-empty">
            <FileText size={32} strokeWidth={1.8} />
            <div>
              <strong>등록된 테스트가 없습니다.</strong>
              <p>하단의 버튼을 눌러 새로운 테스트를 생성해 주세요.</p>
            </div>
          </div>
        ) : (
          <>
                <div className="persona-tests-table-wrap">
                  <table className="persona-tests-table">
                    <thead>
                      <tr>
                        <th className="persona-tests-checkbox-cell">
                          <input
                            type="checkbox"
                            aria-label="현재 페이지 테스트 전체 선택"
                            checked={allRowsSelected}
                            onChange={toggleAllRows}
                          />
                        </th>
                        <th>테스트 명</th>
                        <th>테스트 유형</th>
                        <th>참여 퍼소나 수</th>
                        <th>생성일자</th>
                        <th>진행 상태</th>
                        <th>상세</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRows.map((row) => {
                        const isRetrying = retryingRows.has(row.rowKey);
                        const status = isRetrying ? 'running' : getRowDisplayStatus(row);
                        const selected = selectedRows.has(row.rowKey);
                        const actionLabel = isRetrying ? '생성 중' : status === 'failed' ? '재시도' : '결과보기';
                        const actionDisabled = status === 'running';
                        return (
                          <tr
                            key={row.rowKey}
                            className={`${selected ? 'selected' : ''}${status === 'running' ? ' is-disabled' : ''}`.trim()}
                            onClick={() => openRow(row)}
                          >
                            <td className="persona-tests-checkbox-cell" onClick={(event) => event.stopPropagation()}>
                              <input
                                type="checkbox"
                                aria-label={`${row.name || '테스트'} 선택`}
                                checked={selected}
                                onChange={() => toggleRow(row.rowKey)}
                              />
                            </td>
                            <td>{row.name || '-'}</td>
                            <td>{row.displayType}</td>
                            <td>
                              <span className="persona-tests-persona-count">
                                <UserRound size={16} />
                                {row.persona_count ?? 0}명
                              </span>
                            </td>
                            <td>{formatTestDate(row.created_at)}</td>
                            <td>
                              <span className={`persona-tests-status persona-tests-status--${status}`}>
                                {testStatusLabel(status)}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="persona-tests-row-action"
                                onClick={(event) => handleRowAction(event, row)}
                                disabled={actionDisabled}
                              >
                                {actionLabel}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {listPageCount > 1 ? (
                  <ListPagination
                    page={normalizedListPage}
                    totalPages={listPageCount}
                    onChange={setListPage}
                  />
                ) : null}
              </>
        )}
      </B2BPageShell>
      {showDeleteModal ? (
        <DeleteTestsModal
          deleting={deleting}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
      {showTypeModal ? (
        <TestTypeModal
          selectedType={selectedTestType}
          onSelect={setSelectedTestType}
          onCancel={() => setShowTypeModal(false)}
          onStart={handleStartTest}
        />
      ) : null}
      {showQuickInsightModal ? (
        <QuickInsightModal
          goal={purposeGoal}
          product={purposeProduct}
          length={purposeLength}
          error={error}
          running={purposeRunning}
          onGoalChange={setPurposeGoal}
          onProductChange={setPurposeProduct}
          onLengthChange={setPurposeLength}
          onSubmit={handlePurposeRun}
          onCancel={() => setShowQuickInsightModal(false)}
        />
      ) : null}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
      />
    </B2BLayout>
  );
}

export default TestsListPage;
